import React from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Badge, LoadingSpinner, Table, TextLink, Tooltip } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { EyeIcon, EditIcon, ArchiveIcon } from 'lucide-react';
import type { AddOnGroupRecord } from '../services/addonGroup.types';

interface Props {
    data: AddOnGroupRecord[];
    isLoading?: boolean;
    onRetire?: (id: string) => void;
}

export const AddOnGroupDataTable: React.FC<Props> = ({ data, isLoading = false, onRetire }) => {
    const navigate = useNavigate();

    if (isLoading) {
        return <LoadingSpinner size="xl" className="py-12" message="Loading optional extra groups..." />;
    }

    const columns: ColumnDef<AddOnGroupRecord>[] = [
        {
            accessorKey: 'name',
            header: 'Name',
            cell: (info) => <span className="font-semibold text-sm">{info.getValue() as string}</span>,
        },
        {
            accessorKey: 'description',
            header: 'Description',
            cell: (info) => {
                const desc = info.getValue() as string | null;
                return <span className="text-sm text-mp-foreground">{desc ? desc.slice(0, 60) + (desc.length > 60 ? '…' : '') : '—'}</span>;
            },
        },
        {
            id: 'member_count',
            // List endpoint provides `addons_count` (from Laravel withCount);
            // fall back to `addons?.length` only for test fixtures or detail
            // responses where the full relation is present. See PR #318.
            header: 'Members',
            cell: (info) => (
                <span className="text-sm">
                    {info.row.original.addons_count ?? info.row.original.addons?.length ?? 0}
                </span>
            ),
        },
        {
            accessorKey: 'retired_at',
            header: 'Status',
            cell: (info) => {
                const retiredAt = info.getValue() as string | null;
                return retiredAt
                    ? <Badge variant="error">Retired</Badge>
                    : <Badge variant="success">Active</Badge>;
            },
        },
        {
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            cell: (info) => (
                <div className="flex space-x-2">
                    <Tooltip content="View group">
                        <TextLink
                            onClick={() => navigate(`/smr-admin/addon-groups/${info.row.original.id}`)}
                            color="primary"
                            startIcon={<EyeIcon size={20} />}
                        />
                    </Tooltip>
                    <PermissionGuard permission={{ resource: 'smr', action: 'addon-group.edit' }}>
                        <Tooltip content="Edit group">
                            <TextLink
                                onClick={() => navigate(`/smr-admin/addon-groups/${info.row.original.id}/edit`)}
                                color="secondary"
                                startIcon={<EditIcon size={18} />}
                            />
                        </Tooltip>
                    </PermissionGuard>
                    <PermissionGuard permission={{ resource: 'smr', action: 'addon-group.delete' }}>
                        {!info.row.original.retired_at && onRetire && (
                            <Tooltip content="Retire group">
                                <TextLink
                                    onClick={() => onRetire(info.row.original.id)}
                                    color="warning"
                                    startIcon={<ArchiveIcon size={18} />}
                                />
                            </Tooltip>
                        )}
                    </PermissionGuard>
                </div>
            ),
        },
    ];

    return <Table<AddOnGroupRecord> data={data} columns={columns} enableSorting={false} />;
};
